/* eslint-disable jsx-a11y/no-static-element-interactions */
import { jsx, css } from '@emotion/react';
import { throttle } from 'lodash';
import { createRef, Component } from 'react';

import RecentColorsList from './RecentColorsList';
import ShingleColorsList from './ShingleColorsList';
import Conditional from '../../Conditional';

const shingleColorsScroller = css`
  width: 100%;
  white-space: nowrap;
  height: 200px;
  overflow: hidden;
  @media (min-width: 768px) {
    height: auto;
  }
`;

const recentColorsScroller = css`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  @media (min-width: 768px) {
    height: 200px;
  }
  @media (min-width: 1200px) {
    height: 220px;
  }
`;

const scrollWrapper = css`
  position: relative;
  .icon-chevron-left,
  .icon-chevron-right {
    position: absolute;
    top: 65px;
    transform: translateY(-50%);
    z-index: 10;
    color: white;
    font-size: 45px;
    text-shadow: 1px 2px 2px rgba(0, 0, 0, 0.5);
    cursor: pointer;
    @media (min-width: 768px) {
      top: 75px;
    }
    @media (min-width: 1200px) {
      top: 85px;
    }
  }
  .icon-chevron-left {
    left: 10px;
  }
  .icon-chevron-right {
    right: 10px;
  }
`;

const shingleColorsList = css`
  overflow-x: scroll;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const shingleColorsWrapper = css`
  display: flex;
  width: auto;
  width: fit-content;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const recentColorsWrapper = css`
  width: auto;
  width: fit-content;
  list-style: none;
  margin: 0 -27px;
  padding: 0;
`;

const fadeRight = css`
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  position: absolute;
  top: 0;
  right: 0;
  width: 28px;
  height: 194px;
  z-index: 5;
`;

const fadeLeft = css`
  background: linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
  position: absolute;
  top: 0;
  left: 0;
  width: 28px;
  height: 194px;
  z-index: 5;
`;

class ShingleColorsListController extends Component {
  constructor(props) {
    super(props);
    this.scroller = createRef();
    this.shingleColors = createRef();
    this.leftArrow = createRef();
    this.onScrollThrottled = throttle(this.updateArrows, 100);

    this.state = {
      showLeftScrollerUI: false,
      showRightScrollerUI: true,
    };
  }

  updateArrows() {
    if (this.scroller?.current && this.shingleColors.current) {
      const { scrollWidth } = this.scroller.current;
      const { clientWidth } = this.scroller.current;
      const { scrollLeft } = this.scroller.current;

      const showLeftScrollerUI = scrollLeft > 0;
      const showRightScrollerUI = scrollWidth > clientWidth + scrollLeft;

      if (
        showLeftScrollerUI !== this.state.showLeftScrollerUI ||
        showRightScrollerUI !== this.state.showRightScrollerUI
      ) {
        this.setState({
          showLeftScrollerUI,
          showRightScrollerUI,
        });
      }
    }
  }

  get maxScroll() {
    if (!this.shingleColors.current || !this.scroller.current) {
      return 0;
    }

    return this.shingleColors.current.offsetWidth - this.scroller.current.offsetWidth;
  }

  scrollShingles(difference) {
    this.scroller.current.scrollBy({
      top: 0,
      left: difference,
      behavior: 'smooth',
    });
  }

  componentDidMount() {
    this.onScrollThrottled();
    window.addEventListener('resize', this.onScrollThrottled);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onScrollThrottled);
  }

  componentDidUpdate() {
    this.updateArrows();
  }

  render() {
    const { recentlyViewed, houseView, onColorClick, selectedColor, shingle, address, isPDP } = this.props;
    const { showRightScrollerUI, showLeftScrollerUI } = this.state;
    const scrollDistance = 355;

    return (
      <div css={scrollWrapper}>
        <div css={!recentlyViewed ? shingleColorsScroller : recentColorsScroller}>
          <div css={shingleColorsList} ref={this.scroller} onScroll={() => this.onScrollThrottled()}>
            <div css={fadeRight} style={{ display: showRightScrollerUI ? 'block' : 'none' }} />
            <div css={fadeLeft} style={{ display: showLeftScrollerUI ? 'block' : 'none' }} />
            <i
              className="icon-chevron-left"
              onClick={() => this.scrollShingles(-scrollDistance)}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === 'Space') {
                  this.scrollShingles(-scrollDistance);
                }
              }}
              data-track="view-previous"
              data-track-destination={
                !recentlyViewed ? `${shingle.proper_name} Colors - Swatch View` : 'Recently Viewed'
              }
              ref={this.leftArrow}
              tabIndex="0"
              aria-label="Previous Shingle Color"
              style={{ display: showLeftScrollerUI ? 'block' : 'none' }}
            />
            <i
              className="icon-chevron-right"
              onClick={() => this.scrollShingles(scrollDistance)}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === 'Space') {
                  this.scrollShingles(scrollDistance);
                }
              }}
              data-track="view-more"
              data-track-destination={
                !recentlyViewed ? `${shingle.proper_name} Colors - Swatch View` : 'Recently Viewed'
              }
              tabIndex="0"
              aria-label="Next Shingle Color"
              style={{ display: showRightScrollerUI ? 'block' : 'none' }}
            />
            <div
              css={css`
                position: relative;
                width: fit-content;
                overflow: hidden;
              `}
              ref={this.shingleColors}
            >
              <Conditional if={!recentlyViewed}>
                <ul css={shingleColorsWrapper}>
                  <ShingleColorsList
                    houseView={houseView}
                    onColorClick={onColorClick}
                    selectedColor={selectedColor}
                    shingle={shingle}
                    address={address}
                    isPDP={isPDP}
                  />
                </ul>
              </Conditional>
              <Conditional if={recentlyViewed}>
                <ul css={recentColorsWrapper}>
                  <RecentColorsList recentlyViewed={recentlyViewed} {...this.props} />
                </ul>
              </Conditional>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ShingleColorsListController;
